import { generatePath } from 'react-router-dom';
import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';
import { apiQuery } from 'store/query';
import { addParamsToUrl } from 'utils/query';
import { calculateOffset } from 'utils/helpers';

import {
  EventOrder,
  GenerateCsvRequest,
  EventOrderListRequest,
  EventOrderListResponse,
  EventOrderCreateRequest,
  EventOrderUpdateRequest,
  EventOrderChangeStatusRequest,
  EventOrderChangeTrackingNumberRequest,
} from './types';

export const tagTypes = ['eventOrders'];

export const eventOrdersApi = createApi({
  tagTypes,
  baseQuery: apiQuery,
  reducerPath: 'eventOrdersApi',
  endpoints: (build) => ({
    details: build.query<EventOrder, string>({
      providesTags: tagTypes,
      query: (id) => ({
        method: 'get',
        url: generatePath(URL.EVENT_ORDER, { id }),
      }),
    }),

    delete: build.mutation<void, string>({
      invalidatesTags: tagTypes,
      query: (id) => ({
        method: 'delete',
        url: generatePath(URL.EVENT_ORDER, { id }),
      }),
    }),

    create: build.mutation<EventOrder, EventOrderCreateRequest>({
      invalidatesTags: tagTypes,
      query: (data) => ({
        data,
        method: 'post',
        url: URL.EVENT_ORDERS,
      }),
    }),

    generateCsv: build.query<string, GenerateCsvRequest>({
      providesTags: tagTypes,
      query: ({ type }) => ({
        method: 'get',
        url: addParamsToUrl(URL.EVENT_ORDERS_CSV, { type }),
      }),
    }),

    update: build.mutation<EventOrder, EventOrderUpdateRequest>({
      invalidatesTags: tagTypes,
      query: ({ id, ...data }) => ({
        data,
        method: 'patch',
        url: generatePath(URL.EVENT_ORDER, { id: String(id) }),
      }),
    }),

    changeStatus: build.mutation<EventOrder, EventOrderChangeStatusRequest>({
      invalidatesTags: tagTypes,
      query: ({ id, ...data }) => ({
        data,
        method: 'patch',
        url: generatePath(URL.EVENT_ORDER_CHANGE_STATUS, { id: String(id) }),
      }),
    }),

    changeTrackingNumber: build.mutation<
      EventOrder,
      EventOrderChangeTrackingNumberRequest
    >({
      invalidatesTags: tagTypes,
      query: ({ id, ...data }) => ({
        data,
        method: 'patch',
        url: generatePath(URL.EVENT_ORDER_TRACKING_NUMBER, { id: String(id) }),
      }),
    }),

    list: build.query<EventOrderListResponse, EventOrderListRequest>({
      providesTags: tagTypes,
      query: (query) => {
        const { page = 1, limit = 20, ...otherParams } = query || {};
        const offset = calculateOffset(page, limit);
        const searchParams = {
          limit,
          offset,
          ...otherParams,
        };

        return {
          method: 'get',
          url: addParamsToUrl(URL.EVENT_ORDERS, searchParams),
        };
      },
    }),
  }),
});

export const {
  useListQuery,
  useDetailsQuery,
  useDeleteMutation,
  useCreateMutation,
  useUpdateMutation,
  useGenerateCsvQuery,
  useLazyGenerateCsvQuery,
  useChangeStatusMutation,
  useChangeTrackingNumberMutation,
} = eventOrdersApi;

export type {
  EventOrder,
  EventOrderListRequest,
  EventOrderCreateRequest,
  EventOrderUpdateRequest,
};
